import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { http } from "../../../axios/axios";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";

const InputField = ({
  label,
  name,
  type = "text",
  placeholder,
  register,
  errors = {},
  validation = {},
  as = "input",
  rows = 4,
  options = [],
}) => {
  const inputClass =
    "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm md:px-4 md:py-3 md:text-base outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1.5 block text-sm font-semibold text-slate-700 md:mb-2 md:text-base"
      >
        {label}
        {validation?.required && <span className="text-red-500"> *</span>}
      </label>

      {as === "textarea" ? (
        <textarea
          id={name}
          rows={rows}
          placeholder={placeholder}
          {...register(name, validation)}
          className={`${inputClass} resize-none`}
        />
      ) : as === "select" ? (
        <select
          id={name}
          {...register(name, validation)}
          className={inputClass}
        >
          <option value="">{placeholder}</option>

          {options.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={name}
          type={type}
          placeholder={placeholder}
          {...register(name, validation)}
          className={inputClass}
        />
      )}

      {errors[name] && (
        <p className="mt-1 text-xs text-red-500 md:text-sm">
          {errors[name].message}
        </p>
      )}
    </div>
  );
};

const Complain = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);

  const servieOptions = [
    { value: "installation", label: "Installation" },
    { value: "maintenance", label: "Maintenance" },
    { value: "repair", label: "Repair" },
    { value: "camera", label: "CCTV Camera" },
    { value: "fire_alarm", label: "Fire Alarm" },
    { value: "amc", label: "AMC" },
    { value: "cmc", label: "CMC" },
    { value: "other", label: "Other" },
  ];

  const complaintTypeOptions = [
    { value: "service_request", label: "Service Request" },
    { value: "installation_issue", label: "Installation Issue" },
    { value: "device_not_working", label: "Device Not Working" },
    { value: "camera_issue", label: "CCTV Camera Issue" },
    { value: "recorder_issue", label: "DVR / NVR Issue" },
    { value: "fire_alarm_issue", label: "Fire Alarm Issue" },
    { value: "maintenance", label: "Maintenance" },
    { value: "warranty_claim", label: "Warranty Claim" },
    { value: "billing_issue", label: "Billing Issue" },
    { value: "other", label: "Other" },
  ];

  const MAX_IMAGES = 10;

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > MAX_IMAGES) {
      toast.error(`You can upload a maximum of ${MAX_IMAGES} images`);
      e.target.value = "";
      return;
    }

    const newPhotos = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setPhotos((prev) => [...prev, ...newPhotos]);

    e.target.value = "";
  };

  const MAX_VIDEO_SIZE = 100 * 1024 * 1024;
  const MAX_VIDEOS = 5;

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files);

    if (videos.length + files.length > MAX_VIDEOS) {
      alert(`You can upload a maximum of ${MAX_VIDEOS} videos`);
      e.target.value = "";
      return;
    }

    const selectedVideos = [];

    for (const file of files) {
      if (file.size > MAX_VIDEO_SIZE) {
        toast.error(`${file.name} exceeds the 100 MB limit.`);
        continue;
      }

      selectedVideos.push({
        file,
        preview: URL.createObjectURL(file),
      });
    }

    setVideos((prev) => [...prev, ...selectedVideos]);
    e.target.value = "";
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(photos[index].preview);

    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const removeVideo = (index) => {
    URL.revokeObjectURL(videos[index].preview);
    setVideos((prev) => prev.filter((_, i) => i !== index));
  };

  const fetchBrands = async () => {
    const res = await http.get("/brands?limit=1000");

    if (res.data) {
      setBrands(
        res.data.brands.map((brand) => ({
          value: brand._id,
          label: brand.brandName,
        })),
      );
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);

    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value);
    });
    photos.forEach((photo) => {
      formData.append("photos", photo.file);
    });
    videos.forEach((video) => {
      formData.append("videos", video.file);
    });

    try {
      const res = await http.post("complaints", formData);
      if (res.data.success === true) {
        photos.forEach((photo) => URL.revokeObjectURL(photo.preview));
        videos.forEach((video) => URL.revokeObjectURL(video.preview));
        reset();
        setPhotos([]);
        setVideos([]);
        toast.success(res.data.message || "Complaint submitted successfully.");

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to submit complaint.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  const [seo, setSeo] = useState(null);

  useEffect(() => {
    fetchSeo();
  }, []);

  const fetchSeo = async () => {
    try {
      const { data } = await http.get("/seo/Complaint");
      setSeo(data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const seoData = seo || {
    metaTitle: "",
    metaDescription: "",
    metaTags: [],
    schemaScript: null,
  };

  const schemaScript = seoData?.schemaScript
    ? typeof seoData.schemaScript === "string"
      ? seoData.schemaScript
      : JSON.stringify(seoData.schemaScript)
    : null;

  return (
    <>
      <Helmet>
        <title>{seoData?.metaTitle}</title>

        <meta name="description" content={seoData?.metaDescription} />

        <meta
          name="keywords"
          content={
            Array.isArray(seoData?.metaTags)
              ? seoData?.metaTags.join(", ")
              : seoData?.metaTags
          }
        />

        {schemaScript && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: schemaScript }}
          />
        )}
      </Helmet>

      <div className="min-h-screen bg-white pb-20 font-sans">
        <header className="bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 py-20 px-6 text-center relative overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight font-serif">
              Service <span className="text-blue-400">Complaint</span>
            </h1>

            <p className="hidden md:block mt-6 text-lg text-gray-300 leading-relaxed">
              Experiencing an issue with your security system? Submit your
              complaint or service request, and our support team will review it
              promptly.
            </p>
          </div>
        </header>

        <section className="px-6 py-16">
          <div className="max-w-7xl mx-auto">
            <div className="rounded-md border border-gray-200 bg-white  px-4 py-8 lg:p-8">
              <h2 className="text-2xl font-bold text-slate-900 font-serif">
                Submit a Complaint
              </h2>

              <p className="mt-2 text-gray-600">
                Please provide the required information below.
              </p>

              <form
                onSubmit={handleSubmit(onSubmit)}
                className="mt-8 space-y-6"
              >
                <InputField
                  label="Full Name"
                  name="name"
                  placeholder="Enter your full name"
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Full name is required",
                  }}
                />

                <InputField
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  placeholder="Enter 10-digit phone number"
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Phone number is required",
                    pattern: {
                      value: /^[6-9]\d{9}$/,
                      message: "Enter a valid phone number",
                    },
                  }}
                />
                <InputField
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="john@gmail.com"
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Email ID is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Enter a valid email ID",
                    },
                  }}
                />

                <InputField
                  label="Address"
                  name="address"
                  as="textarea"
                  rows={5}
                  placeholder="Enter your complete address"
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Address is required",
                  }}
                />

                <InputField
                  label="Service"
                  name="service"
                  as="select"
                  placeholder="Select Service"
                  options={servieOptions}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Please select a service",
                  }}
                />

                <InputField
                  label="Brand"
                  name="brand"
                  as="select"
                  placeholder="Select Brand"
                  options={brands}
                  register={register}
                />

                <InputField
                  label="Model"
                  name="model"
                  placeholder="Enter the model number"
                  register={register}
                />
                <InputField
                  label={"Serial Number"}
                  name="serialNumber"
                  placeholder={"Enter the serial number"}
                  register={register}
                />

                <InputField
                  label="Complaint Type"
                  name="complaintType"
                  as="select"
                  placeholder="Select Complaint Type"
                  options={complaintTypeOptions}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Please select a complaint type",
                  }}
                />

                <InputField
                  label="Problem Description"
                  name="description"
                  as="textarea"
                  rows={5}
                  placeholder="Describe the issue you are experiencing..."
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Please describe the problem",
                  }}
                />
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700 md:mb-2 md:text-base">
                    Photos
                  </label>

                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={photos.length >= MAX_IMAGES}
                    onChange={handlePhotoChange}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm"
                  />

                  {photos.length > 0 && (
                    <div className="mt-4 grid gap-4 sm:grid-cols-2  md:grid-cols-4">
                      {photos.map((photo, index) => (
                        <div key={index} className="relative">
                          <img
                            src={photo.preview}
                            alt={`Preview ${index + 1}`}
                            className="h-32 w-full rounded-lg border object-cover"
                          />

                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-red-600 text-white transition hover:bg-red-700"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700 md:mb-2 md:text-base">
                    Videos
                  </label>
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    disabled={videos.length >= MAX_VIDEOS}
                    onChange={handleVideoChange}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm"
                  />

                  <p className="mt-1 text-xs text-gray-500">
                    Upload short videos (MP4, MOV, WebM). Max 100 MB each.
                  </p>

                  {videos.length > 0 && (
                    <div className="mt-4 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                      {videos.map((video, index) => (
                        <div key={index} className="relative">
                          <video
                            src={video.preview}
                            controls
                            className="h-40 w-full rounded-lg border object-cover"
                          />

                          <button
                            type="button"
                            onClick={() => removeVideo(index)}
                            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Installed by us? <span className="text-red-500">*</span>
                  </label>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="true"
                        {...register("installedByUs", {
                          required: "Please select an option",
                        })}
                      />
                      <span>Yes</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="false"
                        {...register("installedByUs", {
                          required: "Please select an option",
                        })}
                      />
                      <span>No</span>
                    </label>
                  </div>

                  {errors.installedByUs && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.installedByUs.message}
                    </p>
                  )}
                </div>
                <InputField
                  label="Installation Date"
                  name="installationDate"
                  type="date"
                  register={register}
                />

                <InputField
                  label="Preferred Support"
                  name="support"
                  as="select"
                  placeholder="Select Preferred Support"
                  options={[
                    { value: "remote", label: "Remote Support" },
                    { value: "onsite", label: "On-site Visit" },
                    { value: "phone", label: "Phone Support" },
                  ]}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Please select your preferred support",
                  }}
                />

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-md bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? "Submitting..." : "Submit Complaint"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Complain;
